import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { BrandLogo } from './BrandLogo';
import { ThemeToggle } from './ThemeToggle';
import { 
  Search, Sparkles, Menu, X, ChevronDown, 
  Bot, Code, Zap, Globe, Users, Network, 
  Package, LayoutGrid, Layers, ArrowRight,
  TrendingUp, Eye, Video, Headphones, GraduationCap, Calendar, 
  Building2, ShoppingBag, ShieldCheck, Factory, Smartphone, MessageSquare,
  BookOpen, Briefcase, Cpu, MapPin, Phone
} from 'lucide-react';
import { BRAND_CONFIG, SAAS_PRODUCTS, SERVICES } from '../data/siteData';

interface HeaderProps {
  onOpenSearch?: () => void;
}

export const Header: React.FC<HeaderProps> = ({ onOpenSearch }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [mobileProductsOpen, setMobileProductsOpen] = useState(false);
  const [mobileSolutionsOpen, setMobileSolutionsOpen] = useState(false);
  const [mobileCompanyOpen, setMobileCompanyOpen] = useState(false);
  
  const [servicesDropdown, setServicesDropdown] = useState(false);
  const [productsDropdown, setProductsDropdown] = useState(false);
  const [solutionsDropdown, setSolutionsDropdown] = useState(false);
  const [companyDropdown, setCompanyDropdown] = useState(false);

  const location = useLocation();

  const isActive = (path: string) => location.pathname === path;
  const isCompanyActive = ['/about', '/portfolio', '/technology', '/blog', '/contact'].some(p => location.pathname.startsWith(p));

  // Group services by category for megamenu
  const aiServices = SERVICES.filter(s => s.category === 'ai-ml');
  const devServices = SERVICES.filter(s => s.category === 'development');
  const automationServices = SERVICES.filter(s => s.category === 'automation');
  const marketingAndTalentServices = SERVICES.filter(s => s.category === 'marketing' || s.category === 'talent');

  const closeDropdowns = () => {
    setServicesDropdown(false);
    setProductsDropdown(false);
    setSolutionsDropdown(false);
    setCompanyDropdown(false);
    setMobileMenuOpen(false);
  };

  return (
    <header className="sticky top-0 z-40 bg-obsidian/95 backdrop-blur-md border-b border-navy-ink/80 transition-all">
      {/* Top Notification Bar for US & Pakistan Markets */}
      <div className="bg-navy-ink/90 border-b border-navy-ink/50 py-1.5 px-4 text-[11px] text-slate-mist">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1.5 text-slate-700 dark:text-slate-200 font-semibold">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
              </span>
              24/7 Global Enterprise Support
            </span>
            <span className="hidden sm:inline text-slate-600">•</span>
            <span className="hidden sm:inline">🇺🇸 USA: +1 (571) 554-9508</span>
            <span className="hidden md:inline text-slate-600">•</span>
            <span className="hidden md:inline">🇵🇰 PK: +92 42 111 165 165</span>
          </div>
          <div className="hidden lg:flex items-center gap-3 text-[11px]">
            <Link to="/portfolio" className="hover:text-white transition-colors">Client Portfolio</Link>
            <span>•</span>
            <Link to="/technology" className="hover:text-white transition-colors">Tech Matrix</Link>
            <span>•</span>
            <Link to="/contact" className="text-amber-400 hover:underline font-bold">Free Architecture Audit →</Link>
          </div>
        </div>
      </div>

      {/* Main Navigation Bar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
        {/* Brand Logo */}
        <Link to="/" onClick={closeDropdowns} className="p-1 hover:opacity-95 transition-opacity">
          <BrandLogo size="md" />
        </Link>

        {/* Desktop Navigation Links: Compact, High-Relevance Nested Clusters */}
        <nav className="hidden lg:flex items-center gap-6 xl:gap-8 text-xs font-semibold tracking-wide">
          {/* 1. Services Mega Dropdown */}
          <div
            className="relative"
            onMouseEnter={() => { setServicesDropdown(true); setProductsDropdown(false); setSolutionsDropdown(false); setCompanyDropdown(false); }}
            onMouseLeave={() => setServicesDropdown(false)}
          >
            <Link
              to="/services"
              className={`flex items-center gap-1.5 transition-colors py-2 cursor-pointer ${
                location.pathname.startsWith('/services')
                  ? 'text-royal-blue font-bold'
                  : 'text-slate-700 dark:text-slate-300 hover:text-royal-blue dark:hover:text-white'
              }`}
            >
              <span>Services</span>
              <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${servicesDropdown ? 'rotate-180 text-royal-blue' : ''}`} />
            </Link>

            {servicesDropdown && (
              <div 
                className="absolute top-full left-1/2 -translate-x-1/4 w-[760px] bg-[#031121] border border-navy-ink rounded-2xl shadow-2xl p-6 grid grid-cols-3 gap-6 animate-fade-in z-50 text-left"
                onMouseEnter={() => setServicesDropdown(true)}
                onMouseLeave={() => setServicesDropdown(false)}
              >
                {/* Column 1: AI & ML */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2 pb-1 border-b border-navy-ink/60 text-sky-400">
                    <Bot className="w-4 h-4" />
                    <span className="font-bold text-[11px] uppercase tracking-wider text-slate-200">AI & Machine Learning</span>
                  </div>
                  <div className="space-y-1">
                    {aiServices.slice(0, 5).map(service => (
                      <Link
                        key={service.id}
                        to={`/services/${service.id}`}
                        onClick={closeDropdowns}
                        className="block p-1.5 rounded-lg hover:bg-navy-ink/70 text-slate-300 hover:text-white text-xs transition-colors"
                      >
                        <div className="font-medium text-slate-200">{service.title}</div>
                        <div className="text-[10px] text-slate-400 line-clamp-1">{service.shortDesc}</div>
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Column 2: Web, App & Software Dev */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2 pb-1 border-b border-navy-ink/60 text-indigo-400">
                    <Code className="w-4 h-4" />
                    <span className="font-bold text-[11px] uppercase tracking-wider text-slate-200">Engineering & Dev</span>
                  </div>
                  <div className="space-y-1">
                    {devServices.map(service => (
                      <Link
                        key={service.id}
                        to={`/services/${service.id}`}
                        onClick={closeDropdowns}
                        className="block p-1.5 rounded-lg hover:bg-navy-ink/70 text-slate-300 hover:text-white text-xs transition-colors"
                      >
                        <div className="font-medium text-slate-200">{service.title}</div>
                        <div className="text-[10px] text-slate-400 line-clamp-1">{service.shortDesc}</div>
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Column 3: Automation & Dedicated Pods */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2 pb-1 border-b border-navy-ink/60 text-amber-400">
                    <Zap className="w-4 h-4" />
                    <span className="font-bold text-[11px] uppercase tracking-wider text-slate-200">Automation & Pods</span>
                  </div>
                  <div className="space-y-1">
                    {automationServices.map(service => (
                      <Link
                        key={service.id}
                        to={`/services/${service.id}`}
                        onClick={closeDropdowns}
                        className="block p-1.5 rounded-lg hover:bg-navy-ink/70 text-slate-300 hover:text-white text-xs transition-colors"
                      >
                        <div className="font-medium text-slate-200">{service.title}</div>
                        <div className="text-[10px] text-slate-400 line-clamp-1">{service.shortDesc}</div>
                      </Link>
                    ))}
                    {marketingAndTalentServices.map(service => (
                      <Link
                        key={service.id}
                        to={`/services/${service.id}`}
                        onClick={closeDropdowns}
                        className="block p-1.5 rounded-lg hover:bg-navy-ink/70 text-slate-300 hover:text-white text-xs transition-colors"
                      >
                        <div className="font-medium text-slate-200">{service.title}</div>
                        <div className="text-[10px] text-slate-400 line-clamp-1">{service.shortDesc}</div>
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Bottom Bar inside Megamenu */}
                <div className="col-span-3 pt-3 mt-1 border-t border-navy-ink/70 flex items-center justify-between text-xs">
                  <span className="text-slate-400">Looking for a custom end-to-end architecture?</span>
                  <Link
                    to="/services"
                    onClick={closeDropdowns}
                    className="text-royal-blue hover:text-sky-300 font-bold flex items-center gap-1"
                  >
                    View All 15 Services &rarr;
                  </Link>
                </div>
              </div>
            )}
          </div>

          {/* 2. SaaS Products Mega Dropdown */}
          <div
            className="relative"
            onMouseEnter={() => { setProductsDropdown(true); setServicesDropdown(false); setSolutionsDropdown(false); setCompanyDropdown(false); }}
            onMouseLeave={() => setProductsDropdown(false)}
          >
            <Link
              to="/products"
              className={`flex items-center gap-1.5 transition-colors py-2 cursor-pointer ${
                location.pathname.startsWith('/products')
                  ? 'text-royal-blue font-bold'
                  : 'text-slate-700 dark:text-slate-300 hover:text-royal-blue dark:hover:text-white'
              }`}
            >
              <span>Products</span>
              <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${productsDropdown ? 'rotate-180 text-royal-blue' : ''}`} />
            </Link>

            {productsDropdown && (
              <div 
                className="absolute top-full left-1/2 -translate-x-1/2 w-[720px] bg-[#031121] border border-navy-ink rounded-2xl shadow-2xl p-6 animate-fade-in z-50 text-left"
                onMouseEnter={() => setProductsDropdown(true)}
                onMouseLeave={() => setProductsDropdown(false)}
              >
                <div className="flex items-center justify-between pb-3 mb-3 border-b border-navy-ink/60">
                  <div className="flex items-center gap-2">
                    <Package className="w-4 h-4 text-bento-gold" />
                    <span className="font-bold text-xs uppercase tracking-wider text-slate-200">10 Ready-to-Deploy Enterprise SaaS Platforms</span>
                  </div>
                  <Link to="/products" onClick={closeDropdowns} className="text-xs text-royal-blue hover:underline font-bold">
                    View Catalog &rarr;
                  </Link>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  {SAAS_PRODUCTS.map((prod) => (
                    <Link
                      key={prod.id}
                      to={prod.id === 'reaicoach' ? '/products/reaicoach' : `/products/${prod.id}`}
                      onClick={closeDropdowns}
                      className="p-2.5 rounded-xl hover:bg-navy-ink/70 border border-transparent hover:border-slate-800 transition-all flex items-start gap-3 group"
                    >
                      <div className="p-2 rounded-lg bg-slate-900 border border-slate-800 text-sky-400 group-hover:bg-royal-blue group-hover:text-white transition-colors shrink-0">
                        {prod.icon === 'Network' && <Network className="w-4 h-4" />}
                        {prod.icon === 'Headphones' && <Headphones className="w-4 h-4" />}
                        {prod.icon === 'GraduationCap' && <GraduationCap className="w-4 h-4" />}
                        {prod.icon === 'Calendar' && <Calendar className="w-4 h-4" />}
                        {prod.icon === 'TrendingUp' && <TrendingUp className="w-4 h-4" />}
                        {prod.icon === 'Video' && <Video className="w-4 h-4" />}
                        {prod.icon === 'Bot' && <Bot className="w-4 h-4" />}
                        {prod.icon === 'Eye' && <Eye className="w-4 h-4" />}
                        {prod.icon === 'PackageCheck' && <Package className="w-4 h-4" />}
                        {prod.icon === 'Users' && <Users className="w-4 h-4" />}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="font-bold text-xs text-white group-hover:text-sky-300">{prod.name}</span>
                          <span className="text-[9px] font-mono text-royal-blue bg-royal-blue/10 px-1.5 py-0.2 rounded">{prod.badge}</span>
                        </div>
                        <p className="text-[11px] text-slate-400 line-clamp-1 mt-0.5">{prod.tagline}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* 3. Solutions Dropdown */}
          <div
            className="relative"
            onMouseEnter={() => { setSolutionsDropdown(true); setServicesDropdown(false); setProductsDropdown(false); setCompanyDropdown(false); }}
            onMouseLeave={() => setSolutionsDropdown(false)}
          >
            <Link
              to="/solutions"
              className={`flex items-center gap-1.5 transition-colors py-2 cursor-pointer ${
                location.pathname.startsWith('/solutions')
                  ? 'text-royal-blue font-bold'
                  : 'text-slate-700 dark:text-slate-300 hover:text-royal-blue dark:hover:text-white'
              }`}
            >
              <span>Solutions</span>
              <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${solutionsDropdown ? 'rotate-180 text-royal-blue' : ''}`} />
            </Link>

            {solutionsDropdown && (
              <div 
                className="absolute top-full left-1/2 -translate-x-1/2 w-[740px] bg-[#031121] border border-navy-ink rounded-2xl shadow-2xl p-6 grid grid-cols-3 gap-6 animate-fade-in z-50 text-left"
                onMouseEnter={() => setSolutionsDropdown(true)}
                onMouseLeave={() => setSolutionsDropdown(false)}
              >
                {/* Column 1: Flagship AI Solutions */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2 pb-1 border-b border-navy-ink/60 text-sky-400">
                    <Sparkles className="w-4 h-4 text-bento-gold" />
                    <span className="font-bold text-[11px] uppercase tracking-wider text-slate-200">Flagship AI</span>
                  </div>
                  <div className="space-y-1">
                    <Link
                      to="/solutions/meetingsense"
                      onClick={closeDropdowns}
                      className="block p-2 rounded-lg hover:bg-navy-ink/70 text-slate-300 hover:text-white text-xs transition-colors"
                    >
                      <div className="font-bold text-slate-200">Meeting Sense Voice AI</div>
                      <div className="text-[10px] text-slate-400">Multimodal facial & speech action items</div>
                    </Link>
                    <Link
                      to="/solutions/inventory-forecasting"
                      onClick={closeDropdowns}
                      className="block p-2 rounded-lg hover:bg-navy-ink/70 text-slate-300 hover:text-white text-xs transition-colors"
                    >
                      <div className="font-bold text-slate-200">Inventory Demand ML</div>
                      <div className="text-[10px] text-slate-400">Predictive demand & natural RAG bot</div>
                    </Link>
                    <Link
                      to="/solutions/environmental-intelligence"
                      onClick={closeDropdowns}
                      className="block p-2 rounded-lg hover:bg-navy-ink/70 text-slate-300 hover:text-white text-xs transition-colors"
                    >
                      <div className="font-bold text-slate-200">Environmental Intelligence</div>
                      <div className="text-[10px] text-slate-400">IoT AQI & atmospheric telemetry</div>
                    </Link>
                  </div>
                </div>

                {/* Column 2: Industry Solutions */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2 pb-1 border-b border-navy-ink/60 text-emerald-400">
                    <Building2 className="w-4 h-4" />
                    <span className="font-bold text-[11px] uppercase tracking-wider text-slate-200">By Industry</span>
                  </div>
                  <div className="space-y-1">
                    <Link
                      to="/solutions/industry"
                      onClick={closeDropdowns}
                      className="block p-2 rounded-lg hover:bg-navy-ink/70 text-slate-300 hover:text-white text-xs transition-colors"
                    >
                      <div className="font-bold text-slate-200">Services by Industry (All)</div>
                      <div className="text-[10px] text-slate-400">Sales, Healthcare, Defense & Retail</div>
                    </Link>
                    <Link
                      to="/solutions/industry/sales-marketing"
                      onClick={closeDropdowns}
                      className="block p-2 rounded-lg hover:bg-navy-ink/70 text-slate-300 hover:text-white text-xs transition-colors"
                    >
                      <div className="font-bold text-slate-200">Sales & Marketing Teams</div>
                      <div className="text-[10px] text-slate-400">Autonomous AI SDRs & coaching</div>
                    </Link>
                    <Link
                      to="/solutions/industry/contact-centers"
                      onClick={closeDropdowns}
                      className="block p-2 rounded-lg hover:bg-navy-ink/70 text-slate-300 hover:text-white text-xs transition-colors"
                    >
                      <div className="font-bold text-slate-200">Contact Centers & BPO</div>
                      <div className="text-[10px] text-slate-400">Conversaigent & 100% QA scoring</div>
                    </Link>
                  </div>
                </div>

                {/* Column 3: Turnkey App Solutions & Marketing */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2 pb-1 border-b border-navy-ink/60 text-purple-400">
                    <Smartphone className="w-4 h-4" />
                    <span className="font-bold text-[11px] uppercase tracking-wider text-slate-200">App Solutions</span>
                  </div>
                  <div className="space-y-1">
                    <Link
                      to="/solutions/app-development/taxi-app"
                      onClick={closeDropdowns}
                      className="block p-2 rounded-lg hover:bg-navy-ink/70 text-slate-300 hover:text-white text-xs transition-colors"
                    >
                      <div className="font-bold text-slate-200">On-Demand Taxi & Ride App</div>
                      <div className="text-[10px] text-slate-400">GPS dispatch & driver routing</div>
                    </Link>
                    <Link
                      to="/solutions/app-development/food-delivery-app"
                      onClick={closeDropdowns}
                      className="block p-2 rounded-lg hover:bg-navy-ink/70 text-slate-300 hover:text-white text-xs transition-colors"
                    >
                      <div className="font-bold text-slate-200">Food Delivery App</div>
                      <div className="text-[10px] text-slate-400">Kitchen KDS & rider tracking</div>
                    </Link>
                    <Link
                      to="/solutions/social/instant-messaging"
                      onClick={closeDropdowns}
                      className="block p-2 rounded-lg hover:bg-navy-ink/70 text-slate-300 hover:text-white text-xs transition-colors"
                    >
                      <div className="font-bold text-slate-200">WhatsApp & Growth Ads</div>
                      <div className="text-[10px] text-slate-400">Cloud API bots & Meta Ads</div>
                    </Link>
                  </div>
                </div>

                {/* Megamenu Footer */}
                <div className="col-span-3 pt-3 border-t border-navy-ink/70 flex items-center justify-between text-xs">
                  <span className="text-slate-400">Looking for a specialized industry application?</span>
                  <Link
                    to="/solutions"
                    onClick={closeDropdowns}
                    className="text-royal-blue hover:text-sky-300 font-bold flex items-center gap-1"
                  >
                    View All Solutions &rarr;
                  </Link>
                </div>
              </div>
            )}
          </div>

          {/* 4. Company Dropdown (Nesting About, Portfolio, Tech Stack, Blog & Offices) */}
          <div
            className="relative"
            onMouseEnter={() => { setCompanyDropdown(true); setServicesDropdown(false); setProductsDropdown(false); setSolutionsDropdown(false); }}
            onMouseLeave={() => setCompanyDropdown(false)}
          >
            <button
              className={`flex items-center gap-1.5 transition-colors py-2 cursor-pointer ${
                isCompanyActive
                  ? 'text-royal-blue font-bold'
                  : 'text-slate-700 dark:text-slate-300 hover:text-royal-blue dark:hover:text-white'
              }`}
            >
              <span>Company</span>
              <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${companyDropdown ? 'rotate-180 text-royal-blue' : ''}`} />
            </button>

            {companyDropdown && (
              <div 
                className="absolute top-full left-1/2 -translate-x-1/2 w-[480px] bg-[#031121] border border-navy-ink rounded-2xl shadow-2xl p-5 animate-fade-in z-50 text-left space-y-2"
                onMouseEnter={() => setCompanyDropdown(true)}
                onMouseLeave={() => setCompanyDropdown(false)}
              >
                <div className="flex items-center justify-between pb-2 border-b border-navy-ink/60 text-xs font-bold text-slate-300">
                  <span>About AMGOC US LLC</span>
                  <span className="text-[10px] text-slate-500 uppercase tracking-wider font-mono">USA • PAKISTAN</span>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <Link
                    to="/about"
                    onClick={closeDropdowns}
                    className="p-2.5 rounded-xl hover:bg-navy-ink/70 border border-transparent hover:border-slate-800 transition-all flex items-start gap-2.5 group"
                  >
                    <div className="p-2 rounded-lg bg-slate-900 text-sky-400 group-hover:bg-royal-blue group-hover:text-white transition-colors shrink-0">
                      <Building2 className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="font-bold text-xs text-white group-hover:text-sky-300">About Us</div>
                      <div className="text-[10px] text-slate-400 line-clamp-1">Leadership, mission & vision</div>
                    </div>
                  </Link>

                  <Link
                    to="/portfolio"
                    onClick={closeDropdowns}
                    className="p-2.5 rounded-xl hover:bg-navy-ink/70 border border-transparent hover:border-slate-800 transition-all flex items-start gap-2.5 group"
                  >
                    <div className="p-2 rounded-lg bg-slate-900 text-indigo-400 group-hover:bg-royal-blue group-hover:text-white transition-colors shrink-0">
                      <LayoutGrid className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="font-bold text-xs text-white group-hover:text-sky-300">Client Portfolio</div>
                      <div className="text-[10px] text-slate-400 line-clamp-1">Enterprise projects & case studies</div>
                    </div>
                  </Link>

                  <Link
                    to="/technology"
                    onClick={closeDropdowns}
                    className="p-2.5 rounded-xl hover:bg-navy-ink/70 border border-transparent hover:border-slate-800 transition-all flex items-start gap-2.5 group"
                  >
                    <div className="p-2 rounded-lg bg-slate-900 text-amber-400 group-hover:bg-royal-blue group-hover:text-white transition-colors shrink-0">
                      <Cpu className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="font-bold text-xs text-white group-hover:text-sky-300">Tech Stack</div>
                      <div className="text-[10px] text-slate-400 line-clamp-1">AI, Cloud, React & Python matrix</div>
                    </div>
                  </Link>

                  <Link
                    to="/blog"
                    onClick={closeDropdowns}
                    className="p-2.5 rounded-xl hover:bg-navy-ink/70 border border-transparent hover:border-slate-800 transition-all flex items-start gap-2.5 group"
                  >
                    <div className="p-2 rounded-lg bg-slate-900 text-emerald-400 group-hover:bg-royal-blue group-hover:text-white transition-colors shrink-0">
                      <BookOpen className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="font-bold text-xs text-white group-hover:text-sky-300">Insights & Blog</div>
                      <div className="text-[10px] text-slate-400 line-clamp-1">AI research & engineering articles</div>
                    </div>
                  </Link>
                </div>

                <div className="pt-2 border-t border-navy-ink/60">
                  <Link
                    to="/contact"
                    onClick={closeDropdowns}
                    className="p-2.5 rounded-xl bg-navy-ink/50 hover:bg-navy-ink border border-navy-ink/70 transition-all flex items-center justify-between group"
                  >
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-rose-400" />
                      <div>
                        <div className="font-bold text-xs text-white">Global Offices & Contact</div>
                        <div className="text-[10px] text-slate-400">McLean VA, USA • Gulberg Lahore, PK</div>
                      </div>
                    </div>
                    <ArrowRight className="w-3.5 h-3.5 text-slate-400 group-hover:text-white group-hover:translate-x-0.5 transition-all" />
                  </Link>
                </div>
              </div>
            )}
          </div>
        </nav>

        {/* Action Buttons & CTA */}
        <div className="hidden sm:flex items-center gap-3">
          <button 
            onClick={onOpenSearch} 
            className="p-2 text-slate-600 dark:text-slate-300 hover:text-royal-blue dark:hover:text-royal-blue transition-colors flex items-center gap-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800" 
            title="Search (Cmd+K)"
          >
            <Search className="w-5 h-5" />
          </button>
          <ThemeToggle />
          <Link
            to="/contact"
            className="inline-flex items-center gap-1.5 bg-royal-blue hover:bg-blue-600 text-white text-xs font-bold px-4 py-2 rounded-xl transition-all shadow-sm shadow-blue-500/20 active:scale-95"
          >
            <span>Book Consultation</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        {/* Mobile Hamburger Toggle */}
        <div className="lg:hidden flex items-center gap-2">
          <div className="sm:hidden flex gap-2">
            <button onClick={onOpenSearch} className="p-2 text-slate-600 dark:text-slate-300 hover:text-royal-blue dark:hover:text-royal-blue transition-colors rounded-full" aria-label="Open search">
              <Search className="w-5 h-5" />
            </button>
            <ThemeToggle />
          </div>
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:text-white p-2"
            id="mobile-menu-toggle"
            aria-label="Toggle navigation menu"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Drawer Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-[#031121] border-b border-navy-ink px-4 py-5 space-y-3 animate-fade-in text-sm font-semibold max-h-[85vh] overflow-y-auto">
          <Link
            to="/"
            onClick={closeDropdowns}
            className="block py-2 text-white border-b border-navy-ink/50"
          >
            Home
          </Link>

          {/* Mobile Services Collapsible */}
          <div className="border-b border-navy-ink/50 py-1">
            <div 
              className="flex items-center justify-between py-2 text-slate-200 cursor-pointer"
              onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
            >
              <span>Services</span>
              <ChevronDown className={`w-4 h-4 transition-transform ${mobileServicesOpen ? 'rotate-180' : ''}`} />
            </div>
            {mobileServicesOpen && (
              <div className="pl-3 py-1 space-y-1.5 text-xs text-slate-300 bg-slate-950/60 rounded-lg p-2 my-1 border border-navy-ink/40">
                <Link to="/services" onClick={closeDropdowns} className="block py-1 text-royal-blue font-bold">
                  → All 15 Services Overview
                </Link>
                <div className="text-[10px] uppercase font-bold text-sky-400 pt-1">AI & Machine Learning</div>
                {aiServices.map(s => (
                  <Link key={s.id} to={`/services/${s.id}`} onClick={closeDropdowns} className="block py-0.5 pl-2 text-slate-300 hover:text-white">
                    • {s.title}
                  </Link>
                ))}
                <div className="text-[10px] uppercase font-bold text-indigo-400 pt-1">Web & Software Dev</div>
                {devServices.map(s => (
                  <Link key={s.id} to={`/services/${s.id}`} onClick={closeDropdowns} className="block py-0.5 pl-2 text-slate-300 hover:text-white">
                    • {s.title}
                  </Link>
                ))}
                <div className="text-[10px] uppercase font-bold text-amber-400 pt-1">Automation & Growth</div>
                {automationServices.map(s => (
                  <Link key={s.id} to={`/services/${s.id}`} onClick={closeDropdowns} className="block py-0.5 pl-2 text-slate-300 hover:text-white">
                    • {s.title}
                  </Link>
                ))}
                <Link to="/services/hire-a-developer" onClick={closeDropdowns} className="block py-0.5 pl-2 text-indigo-300 hover:text-white">
                  • Hire Dedicated Developers
                </Link>
              </div>
            )}
          </div>

          {/* Mobile Products Collapsible */}
          <div className="border-b border-navy-ink/50 py-1">
            <div 
              className="flex items-center justify-between py-2 text-slate-200 cursor-pointer"
              onClick={() => setMobileProductsOpen(!mobileProductsOpen)}
            >
              <span>10 SaaS Products</span>
              <ChevronDown className={`w-4 h-4 transition-transform ${mobileProductsOpen ? 'rotate-180' : ''}`} />
            </div>
            {mobileProductsOpen && (
              <div className="pl-3 py-1 space-y-1 text-xs text-slate-300 bg-slate-950/60 rounded-lg p-2 my-1 border border-navy-ink/40">
                <Link to="/products" onClick={closeDropdowns} className="block py-1 text-royal-blue font-bold">
                  → View Full Products Catalog
                </Link>
                {SAAS_PRODUCTS.map(p => (
                  <Link key={p.id} to={p.id === 'reaicoach' ? '/products/reaicoach' : `/products/${p.id}`} onClick={closeDropdowns} className="block py-1 pl-2 text-slate-300 hover:text-white">
                    • {p.name} <span className="text-[10px] text-slate-400">({p.badge})</span>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Mobile Solutions Collapsible */}
          <div className="border-b border-navy-ink/50 py-1">
            <div 
              className="flex items-center justify-between py-2 text-slate-200 cursor-pointer"
              onClick={() => setMobileSolutionsOpen(!mobileSolutionsOpen)}
            >
              <span>Solutions</span>
              <ChevronDown className={`w-4 h-4 transition-transform ${mobileSolutionsOpen ? 'rotate-180' : ''}`} />
            </div>
            {mobileSolutionsOpen && (
              <div className="pl-3 py-1 space-y-1 text-xs text-slate-300 bg-slate-950/60 rounded-lg p-2 my-1 border border-navy-ink/40">
                <Link to="/solutions" onClick={closeDropdowns} className="block py-1 text-royal-blue font-bold">
                  → All Solutions Overview
                </Link>
                <div className="text-[10px] uppercase font-bold text-bento-gold pt-1">Flagship AI</div>
                <Link to="/solutions/meetingsense" onClick={closeDropdowns} className="block py-0.5 pl-2 text-slate-300">
                  • Meeting Sense Voice AI
                </Link>
                <Link to="/solutions/inventory-forecasting" onClick={closeDropdowns} className="block py-0.5 pl-2 text-slate-300">
                  • Inventory Demand ML
                </Link>
                <Link to="/solutions/environmental-intelligence" onClick={closeDropdowns} className="block py-0.5 pl-2 text-slate-300">
                  • Environmental Intelligence (AQI)
                </Link>
                
                <div className="text-[10px] uppercase font-bold text-emerald-400 pt-1">By Industry</div>
                <Link to="/solutions/industry" onClick={closeDropdowns} className="block py-0.5 pl-2 text-slate-300">
                  • Services by Industry
                </Link>
                
                <div className="text-[10px] uppercase font-bold text-purple-400 pt-1">App Development Solutions</div>
                <Link to="/solutions/app-development/taxi-app" onClick={closeDropdowns} className="block py-0.5 pl-2 text-slate-300">
                  • Taxi & Ride-Hailing App
                </Link>
                <Link to="/solutions/app-development/food-delivery-app" onClick={closeDropdowns} className="block py-0.5 pl-2 text-slate-300">
                  • Food Delivery Platform
                </Link>
                <Link to="/solutions/social/instant-messaging" onClick={closeDropdowns} className="block py-0.5 pl-2 text-slate-300">
                  • WhatsApp Cloud API Bots
                </Link>
              </div>
            )}
          </div>

          {/* Mobile Company Collapsible */}
          <div className="border-b border-navy-ink/50 py-1">
            <div 
              className="flex items-center justify-between py-2 text-slate-200 cursor-pointer"
              onClick={() => setMobileCompanyOpen(!mobileCompanyOpen)}
            >
              <span>Company & Resources</span>
              <ChevronDown className={`w-4 h-4 transition-transform ${mobileCompanyOpen ? 'rotate-180' : ''}`} />
            </div>
            {mobileCompanyOpen && (
              <div className="pl-3 py-1 space-y-1 text-xs text-slate-300 bg-slate-950/60 rounded-lg p-2 my-1 border border-navy-ink/40">
                <Link to="/about" onClick={closeDropdowns} className="block py-1 pl-2 text-slate-300 hover:text-white">
                  • About AMGOC US LLC
                </Link>
                <Link to="/portfolio" onClick={closeDropdowns} className="block py-1 pl-2 text-slate-300 hover:text-white">
                  • Client Portfolio & Case Studies
                </Link>
                <Link to="/technology" onClick={closeDropdowns} className="block py-1 pl-2 text-slate-300 hover:text-white">
                  • Technology Stack Matrix
                </Link>
                <Link to="/blog" onClick={closeDropdowns} className="block py-1 pl-2 text-slate-300 hover:text-white">
                  • Insights & AI Blog
                </Link>
                <Link to="/contact" onClick={closeDropdowns} className="block py-1 pl-2 text-slate-300 hover:text-white">
                  • Global Offices (USA & PK)
                </Link>
              </div>
            )}
          </div>

          <Link
            to="/contact"
            onClick={closeDropdowns}
            className="block py-2.5 text-amber-400 font-bold text-center bg-royal-blue/20 rounded-xl border border-royal-blue/30 mt-2"
          >
            Book Free Consultation →
          </Link>
        </div>
      )}
    </header>
  );
};


